import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import {
  IGroupedWaitingRoom,
  IWaitingRoom,
  IMassActionsPayload,
  IWaitingRoomsGroup,
} from "src/Pages/Company/Pending/PendingBenefits/types";
import { SelectedRowsContext } from "./context";
import {
  buildWaitingRoomsPayload,
  countAcceptableWaitingRooms,
  countCancellableWaitingRooms,
  getAcceptableDocumentIds,
  getAllProducts,
  getConditionsIds,
  getFilteredAcceptableConditions,
  handleSalaryDeductionMap,
  handleWaitingRoomsMap,
} from "./utils";

export const SelectedRowsProvider = ({ children }: PropsWithChildren) => {
  const [selectedWaitingRooms, setSelectedWaitingRooms] = useState<
    Map<IWaitingRoom["id"], IWaitingRoom>
  >(new Map());

  const [selectedSalaryDeductions, setSelectedSalaryDeductions] = useState<
    Map<IWaitingRoomsGroup["groupId"], boolean>
  >(new Map());

  const resetSelection = useCallback(() => {
    setSelectedSalaryDeductions(new Map());
    setSelectedWaitingRooms(new Map());
  }, []);

  const handleSelectAllRows = useCallback(
    (rows: IGroupedWaitingRoom[], value: boolean) => {
      if (value) {
        setSelectedSalaryDeductions(
          new Map(
            [...rows]
              .map((row) => row.waitingRoomsGroup)
              .filter((group) => group != null)
              .map((group) => [
                group.groupId,
                !group.salaryDeductionAccepted && group.documentActive,
              ])
          )
        );

        const waitingRoomsMap = new Map();
        rows.forEach((row) => {
          handleWaitingRoomsMap(row, value, waitingRoomsMap);
        });
        setSelectedWaitingRooms(waitingRoomsMap);
      } else {
        resetSelection();
      }
    },
    [resetSelection]
  );
  const handleSelectRow = useCallback(
    (row: IGroupedWaitingRoom, value: boolean) => {
      const group = row.waitingRoomsGroup;
      if (group !== null) {
        setSelectedSalaryDeductions((prev) => {
          const next = new Map(prev);
          handleSalaryDeductionMap(group, value, next);
          return next;
        });
      }

      setSelectedWaitingRooms((prev) => {
        const next = new Map(prev);
        handleWaitingRoomsMap(row, value, next);
        return next;
      });
    },
    []
  );

  const isSalaryDeductionSelected = useCallback(
    (item: IWaitingRoomsGroup) => selectedSalaryDeductions.has(item.groupId),
    [selectedSalaryDeductions]
  );
  const selectSalaryDeduction = useCallback(
    (group: IWaitingRoomsGroup) => (value: boolean) => {
      setSelectedSalaryDeductions((prev) => {
        const next = new Map(prev);
        handleSalaryDeductionMap(group, value, next);
        return next;
      });
    },
    []
  );

  const isWaitingRoomSelected = useCallback(
    (item: IWaitingRoom) => selectedWaitingRooms.has(item.id),
    [selectedWaitingRooms]
  );
  const selectWaitingRoom = useCallback(
    (item: IWaitingRoom) => (value: boolean) => {
      setSelectedWaitingRooms((prev) => {
        const next = new Map(prev);
        if (value) {
          next.set(item.id, item);
        } else {
          next.delete(item.id);
        }
        return next;
      });
    },
    []
  );

  const getRowState = useCallback(
    (row: IGroupedWaitingRoom) => {
      const isChecked =
        getAllProducts(row).every((item) => isWaitingRoomSelected(item)) &&
        (!row.waitingRoomsGroup ||
          isSalaryDeductionSelected(row.waitingRoomsGroup));

      let isIndeterminate = false;

      if (!isChecked) {
        isIndeterminate =
          getAllProducts(row).some((item) => isWaitingRoomSelected(item)) ||
          (!!row.waitingRoomsGroup &&
            isSalaryDeductionSelected(row.waitingRoomsGroup));
      }
      return { isChecked, isIndeterminate };
    },
    [isSalaryDeductionSelected, isWaitingRoomSelected]
  );

  const getAllRowsState = useCallback(
    (rows: IGroupedWaitingRoom[]) => {
      if (selectedSalaryDeductions.size > 0 || selectedWaitingRooms.size > 0) {
        const isChecked = rows.every((row) => getRowState(row).isChecked);
        return {
          isChecked,
          isIndeterminate: !isChecked,
        };
      }
      return {
        isChecked: false,
        isIndeterminate: false,
      };
    },
    [getRowState, selectedSalaryDeductions.size, selectedWaitingRooms.size]
  );

  const selectedSalaryDeductionsCount = selectedSalaryDeductions.size;
  const selectedCancellableWaitingRoomsCount = useMemo(
    () => countCancellableWaitingRooms(selectedWaitingRooms),
    [selectedWaitingRooms]
  );
  const selectedAcceptableSalaryDeductionsCount = useMemo(
    () => [...selectedSalaryDeductions.values()].filter(Boolean).length,
    [selectedSalaryDeductions]
  );
  const selectedAcceptableWaitingRoomsCount = useMemo(
    () => countAcceptableWaitingRooms(selectedWaitingRooms),
    [selectedWaitingRooms]
  );

  const filteredAcceptableWaitingRoomsCount = useCallback(
    (conditionTypes: string[]) =>
      [...selectedWaitingRooms.values()]
        .map(
          ({ conditions }) =>
            getFilteredAcceptableConditions(conditionTypes, conditions).length >
            0
        )
        .filter(Boolean).length,
    [selectedWaitingRooms]
  );

  const getMassActionData = useCallback(
    (toAccept?: boolean) => ({
      documents: toAccept
        ? getAcceptableDocumentIds(selectedSalaryDeductions)
        : [...selectedSalaryDeductions.keys()],
      waitingRooms: [...selectedWaitingRooms.entries()]
        .filter(([, { cancellable, conditions }]) =>
          toAccept
            ? conditions.filter(
                ({ accepted, conditionAcceptable }) =>
                  conditionAcceptable && !accepted
              )
            : cancellable
        )
        .map(([waitingRoomId, { conditions }]) => ({
          waitingRoomId,
          conditionIds: toAccept
            ? getConditionsIds(
                conditions.filter(
                  ({ accepted, conditionAcceptable }) =>
                    conditionAcceptable && !accepted
                )
              )
            : [],
        })),
    }),
    [selectedSalaryDeductions, selectedWaitingRooms]
  );

  const getFilteredMassActionData = useCallback(
    (
      salaryDeduction: boolean,
      conditionTypes: string[]
    ): IMassActionsPayload => ({
      documents:
        salaryDeduction && selectedAcceptableSalaryDeductionsCount > 0
          ? getAcceptableDocumentIds(selectedSalaryDeductions)
          : [],
      waitingRooms:
        conditionTypes?.length > 0 &&
        filteredAcceptableWaitingRoomsCount(conditionTypes) > 0
          ? buildWaitingRoomsPayload(selectedWaitingRooms, conditionTypes)
          : [],
    }),
    [
      filteredAcceptableWaitingRoomsCount,
      selectedAcceptableSalaryDeductionsCount,
      selectedSalaryDeductions,
      selectedWaitingRooms,
    ]
  );

  const providerValue = useMemo(
    () => ({
      filteredAcceptableWaitingRoomsCount,
      getAllRowsState,
      getFilteredMassActionData,
      getMassActionData,
      getRowState,
      handleSelectAllRows,
      handleSelectRow,
      isSalaryDeductionSelected,
      isWaitingRoomSelected,
      resetSelection,
      selectedAcceptableSalaryDeductionsCount,
      selectedAcceptableWaitingRoomsCount,
      selectedSalaryDeductionsCount,
      selectedCancellableWaitingRoomsCount,
      selectSalaryDeduction,
      selectWaitingRoom,
    }),
    [
      filteredAcceptableWaitingRoomsCount,
      getAllRowsState,
      getFilteredMassActionData,
      getMassActionData,
      getRowState,
      handleSelectAllRows,
      handleSelectRow,
      isSalaryDeductionSelected,
      isWaitingRoomSelected,
      resetSelection,
      selectSalaryDeduction,
      selectWaitingRoom,
      selectedAcceptableSalaryDeductionsCount,
      selectedAcceptableWaitingRoomsCount,
      selectedSalaryDeductionsCount,
      selectedCancellableWaitingRoomsCount,
    ]
  );

  return (
    <SelectedRowsContext.Provider value={providerValue}>
      {children}
    </SelectedRowsContext.Provider>
  );
};
